import React, { useState, useEffect } from 'react';
import { Table, Select, message, Button, Space, Modal, Form, Input, Row, Col } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { PageTitle, StyledCard, SearchableSelect, FormLabel } from '../common';
import { theme } from '../../theme';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;

interface IProductor {
  id: number;
  nc: string;
  denominacion: string;
}

interface IEstablecimiento {
  id: number;
  descripcion: string;
  productor_id: number;
  productor: string;
  provincia_id: number;
  provincia: string;
  departamento_id: number;
  departamento: string;
}

interface ILote {
  id: number;
  descripcion: string;
  establecimiento_id: number;
}

interface IProvincia {
  id: number;
  provincia: string;
}

interface IDepartamento {
  id: number;
  departamento: string;
}

const ProductorEstablecimientoLote: React.FC = () => {
  // Auth
  const { getAccessTokenSilently, user } = useAuth0();
  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;

  // Estados principales
  const [productores, setProductores] = useState<IProductor[]>([]);
  const [establecimientos, setEstablecimientos] = useState<IEstablecimiento[]>([]);
  const [lotes, setLotes] = useState<ILote[]>([]);
  const [provincias, setProvincias] = useState<IProvincia[]>([]);
  const [departamentos, setDepartamentos] = useState<IDepartamento[]>([]);

  // Selecciones
  const [productorSeleccionado, setProductorSeleccionado] = useState<number | null>(null);
  const [establecimientoSeleccionado, setEstablecimientoSeleccionado] = useState<number | null>(null);

  // Loading
  const [loadingEstablecimientos, setLoadingEstablecimientos] = useState(false);
  const [loadingLotes, setLoadingLotes] = useState(false);

  // Modales
  const [modalEstablecimiento, setModalEstablecimiento] = useState(false);
  const [modalLote, setModalLote] = useState(false);
  const [editingEstablecimiento, setEditingEstablecimiento] = useState<IEstablecimiento | null>(null);
  const [editingLote, setEditingLote] = useState<ILote | null>(null);

  // Forms
  const [formEstablecimiento] = Form.useForm();
  const [formLote] = Form.useForm();

  // ===== CARGA INICIAL =====

  useEffect(() => {
    cargarProductores();
    cargarProvincias();
  }, []);

  const cargarProductores = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_PADRON_URL}/leer_padron/${id_empresa}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error('Error al cargar productores');
      const data = await response.json();
      setProductores(data);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los productores');
    }
  };

  const cargarProvincias = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_COMMON_URL}/listar_provincias`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error('Error al cargar provincias');
      const data = await response.json();
      setProvincias(data);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las provincias');
    }
  };

  const cargarDepartamentos = async (provinciaId: number) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_COMMON_URL}/listar_departamentos/${provinciaId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error('Error al cargar departamentos');
      const data = await response.json();
      setDepartamentos(data);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los departamentos');
    }
  };

  // ===== CARGAR ESTABLECIMIENTOS Y LOTES =====

  useEffect(() => {
    if (productorSeleccionado) {
      cargarEstablecimientos(productorSeleccionado);
    } else {
      setEstablecimientos([]);
      setLotes([]);
      setEstablecimientoSeleccionado(null);
    }
  }, [productorSeleccionado]);

  useEffect(() => {
    if (establecimientoSeleccionado) {
      cargarLotes(establecimientoSeleccionado);
    } else {
      setLotes([]);
    }
  }, [establecimientoSeleccionado]);

  const cargarEstablecimientos = async (productorId: number) => {
    setLoadingEstablecimientos(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BALANZA_URL}/establecimiento/${id_empresa}/${productorId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error('Error al cargar establecimientos');
      const data = await response.json();
      setEstablecimientos(data);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los establecimientos');
    } finally {
      setLoadingEstablecimientos(false);
    }
  };

  const cargarLotes = async (establecimientoId: number) => {
    setLoadingLotes(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BALANZA_URL}/lote/${id_empresa}/${establecimientoId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error('Error al cargar lotes');
      const data = await response.json();
      setLotes(data);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los lotes');
    } finally {
      setLoadingLotes(false);
    }
  };

  // ===== ESTABLECIMIENTO - CREAR/EDITAR =====

  const abrirModalEstablecimiento = (establecimiento?: IEstablecimiento) => {
    if (establecimiento) {
      setEditingEstablecimiento(establecimiento);
      formEstablecimiento.setFieldsValue({
        descripcion: establecimiento.descripcion,
        provincia_id: establecimiento.provincia_id,
        departamento_id: establecimiento.departamento_id,
      });
      cargarDepartamentos(establecimiento.provincia_id);
    } else {
      setEditingEstablecimiento(null);
      formEstablecimiento.resetFields();
      setDepartamentos([]);
    }
    setModalEstablecimiento(true);
  };

  const handleGuardarEstablecimiento = async (values: any) => {
    if (!productorSeleccionado) {
      message.error('Debe seleccionar un productor');
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      
      let url: string;
      let data: any;
      
      if (editingEstablecimiento) {
        // Editar - NO envía id_productor en el body
        url = `${process.env.REACT_APP_RELACION_URL}/editar_relacion_establecimientos/${id_empresa}/${productorSeleccionado}/${editingEstablecimiento.id}`;
        data = {
          id_provincia: values.provincia_id,
          id_departamento: values.departamento_id,
          descripcion: values.descripcion.trim(),
        };
      } else {
        // Crear - SÍ envía id_productor en el body
        url = `${process.env.REACT_APP_RELACION_URL}/crear_relacion_establecimiento/${id_empresa}`;
        data = {
          id_productor: productorSeleccionado,
          id_provincia: values.provincia_id,
          id_departamento: values.departamento_id,
          descripcion: values.descripcion.trim(),
        };
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        message.success(result.message || (editingEstablecimiento ? 'Establecimiento actualizado correctamente' : 'Establecimiento creado correctamente'));
        setModalEstablecimiento(false);
        formEstablecimiento.resetFields();
        cargarEstablecimientos(productorSeleccionado);
      } else {
        throw new Error(result.message || 'Error al guardar establecimiento');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error(error instanceof Error ? error.message : 'Error al guardar el establecimiento');
    }
  };

  // Nota: El backend no tiene endpoint de eliminar implementado
  // Esta función está preparada para cuando se implemente
  const handleEliminarEstablecimiento = async (establecimiento: IEstablecimiento) => {
    message.warning('La funcionalidad de eliminar establecimiento no está disponible aún.');
    // TODO: Implementar cuando el backend tenga el endpoint
  };

  // ===== LOTE - CREAR/EDITAR =====

  const abrirModalLote = (lote?: ILote) => {
    if (!establecimientoSeleccionado) {
      message.warning('Primero debe seleccionar un establecimiento en la tabla');
      return;
    }

    if (lote) {
      setEditingLote(lote);
      formLote.setFieldsValue({
        descripcion: lote.descripcion,
      });
    } else {
      setEditingLote(null);
      formLote.resetFields();
    }
    setModalLote(true);
  };

  const handleGuardarLote = async (values: any) => {
    if (!establecimientoSeleccionado) {
      message.error('Debe seleccionar un establecimiento');
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const data = {
        descripcion: values.descripcion.trim(),
        id_establecimiento: establecimientoSeleccionado,
      };

      const url = editingLote
        ? `${process.env.REACT_APP_RELACION_URL}/editar_relacion_lote/${id_empresa}/${establecimientoSeleccionado}/${editingLote.id}`
        : `${process.env.REACT_APP_RELACION_URL}/crear_relacion_lote/${id_empresa}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (result.status === 'success') {
        message.success(result.message || (editingLote ? 'Lote actualizado correctamente' : 'Lote creado correctamente'));
        setModalLote(false);
        formLote.resetFields();
        cargarLotes(establecimientoSeleccionado);
      } else {
        throw new Error(result.message || 'Error al guardar lote');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error(error instanceof Error ? error.message : 'Error al guardar el lote');
    }
  };

  // Nota: El backend no tiene endpoint de eliminar implementado
  // Esta función está preparada para cuando se implemente
  const handleEliminarLote = async (lote: ILote) => {
    message.warning('La funcionalidad de eliminar lote no está disponible aún.');
    // TODO: Implementar cuando el backend tenga el endpoint
  };

  // ===== COLUMNAS DE TABLAS =====

  const columnasEstablecimientos: ColumnsType<IEstablecimiento> = [
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 250,
      sorter: (a, b) => a.descripcion.localeCompare(b.descripcion),
    },
    {
      title: 'Provincia',
      dataIndex: 'provincia',
      key: 'provincia',
      width: 200,
      filters: Array.from(new Set(establecimientos.map(e => e.provincia))).map(p => ({
        text: p,
        value: p,
      })),
      onFilter: (value: any, record) => record.provincia === value,
    },
    {
      title: 'Departamento',
      dataIndex: 'departamento',
      key: 'departamento',
      width: 200,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 80,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => abrirModalEstablecimiento(record)}
            title="Editar"
          />
          {/* Botón Eliminar deshabilitado - funcionalidad no disponible en backend */}
          {/* <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleEliminarEstablecimiento(record)}
            title="Eliminar (no disponible)"
            disabled
          /> */}
        </Space>
      ),
    },
  ];

  const columnasLotes: ColumnsType<ILote> = [
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      sorter: (a, b) => a.descripcion.localeCompare(b.descripcion),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 80,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => abrirModalLote(record)}
            title="Editar"
          />
          {/* Botón Eliminar deshabilitado - funcionalidad no disponible en backend */}
          {/* <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleEliminarLote(record)}
            title="Eliminar (no disponible)"
            disabled
          /> */}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0' }}>
      <PageTitle>Establecimientos y Lotes</PageTitle>

      {/* Selector de Productor */}
      <StyledCard title="Seleccionar Productor">
        <Row gutter={16}>
          <Col span={24}>
            <FormLabel>Productor:</FormLabel>
            <SearchableSelect
              placeholder="Buscar por N° Cliente o Razón Social"
              value={productorSeleccionado}
              onChange={(value) => {
                setProductorSeleccionado(value);
                setEstablecimientoSeleccionado(null);
              }}
              options={productores.map((p) => ({
                value: p.id,
                label: `${p.nc} - ${p.denominacion}`,
              }))}
            />
          </Col>
        </Row>
      </StyledCard>

      {/* Tabla de Establecimientos */}
      {productorSeleccionado && (
        <StyledCard
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Establecimientos</span>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => abrirModalEstablecimiento()}
                size="middle"
                style={{
                  backgroundColor: theme.colors.success,
                  borderColor: theme.colors.success,
                }}
              >
                Nuevo Establecimiento
              </Button>
            </div>
          }
          bodyStyle={{ padding: 0 }}
        >
          <Table
            scroll={{ x: 'max-content' }}
            columns={columnasEstablecimientos}
            dataSource={establecimientos}
            rowKey="id"
            loading={loadingEstablecimientos}
            size="middle"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            onRow={(record) => ({
              onClick: () => setEstablecimientoSeleccionado(record.id),
              style: {
                cursor: 'pointer',
                backgroundColor:
                  record.id === establecimientoSeleccionado ? '#e6f7ff' : undefined,
              },
            })}
            locale={{ emptyText: 'No hay establecimientos registrados' }}
          />
        </StyledCard>
      )}

      {/* Tabla de Lotes */}
      {establecimientoSeleccionado && (
        <StyledCard
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                Lotes -{' '}
                {establecimientos.find((e) => e.id === establecimientoSeleccionado)?.descripcion}
              </span>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => abrirModalLote()}
                size="middle"
                style={{
                  backgroundColor: theme.colors.success,
                  borderColor: theme.colors.success,
                }}
              >
                Nuevo Lote
              </Button>
            </div>
          }
          bodyStyle={{ padding: 0 }}
        >
          <Table
            scroll={{ x: 'max-content' }}
            columns={columnasLotes}
            dataSource={lotes}
            rowKey="id"
            loading={loadingLotes}
            size="middle"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: 'No hay lotes registrados' }}
          />
        </StyledCard>
      )}

      {/* Modal Establecimiento */}
      <Modal
        title={editingEstablecimiento ? 'Editar Establecimiento' : 'Nuevo Establecimiento'}
        open={modalEstablecimiento}
        onCancel={() => {
          setModalEstablecimiento(false);
          formEstablecimiento.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={formEstablecimiento}
          layout="vertical"
          onFinish={handleGuardarEstablecimiento}
        >
          <Form.Item
            label="Descripción"
            name="descripcion"
            rules={[{ required: true, message: 'Ingrese la descripción del establecimiento' }]}
          >
            <Input placeholder="Ej: Campo Norte" size="large" />
          </Form.Item>

          <Form.Item
            label="Provincia"
            name="provincia_id"
            rules={[{ required: true, message: 'Seleccione una provincia' }]}
          >
            <Select
              placeholder="Seleccione provincia"
              size="large"
              onChange={(value) => {
                cargarDepartamentos(value);
                formEstablecimiento.setFieldsValue({ departamento_id: undefined });
              }}
            >
              {provincias.map((p) => (
                <Option key={p.id} value={p.id}>
                  {p.provincia}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Departamento"
            name="departamento_id"
            rules={[{ required: true, message: 'Seleccione un departamento' }]}
          >
            <Select placeholder="Seleccione departamento" size="large">
              {departamentos.map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.departamento}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalEstablecimiento(false)}>Cancelar</Button>
              <Button
                type="primary"
                htmlType="submit"
                style={{
                  backgroundColor: theme.colors.success,
                  borderColor: theme.colors.success,
                }}
              >
                {editingEstablecimiento ? 'Actualizar' : 'Guardar'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Lote */}
      <Modal
        title={editingLote ? 'Editar Lote' : 'Nuevo Lote'}
        open={modalLote}
        onCancel={() => {
          setModalLote(false);
          formLote.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={formLote} layout="vertical" onFinish={handleGuardarLote}>
          <Form.Item
            label="Descripción"
            name="descripcion"
            rules={[{ required: true, message: 'Ingrese la descripción del lote' }]}
          >
            <Input placeholder="Ej: Lote 1A" size="large" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalLote(false)}>Cancelar</Button>
              <Button
                type="primary"
                htmlType="submit"
                style={{
                  backgroundColor: theme.colors.success,
                  borderColor: theme.colors.success,
                }}
              >
                {editingLote ? 'Actualizar' : 'Guardar'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductorEstablecimientoLote;

